import { stringToColor } from './alnumIdToColor';

export const hexToRgbArray = (hex: string) => {
  // If a three-character hexcolor, make six-character
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((hexPart) => hexPart + hexPart)
      .join('');
  }
  // eslint-disable-next-line unicorn/better-regex
  const aRgbHex = hex.match(/([^#].)/g);
  if (aRgbHex && aRgbHex.length === 1) {
    aRgbHex[0] = aRgbHex[0];
    aRgbHex[1] = aRgbHex[0];
    aRgbHex[2] = aRgbHex[0];
  }
  let aRgb = [255, 255, 255];
  if (aRgbHex) {
    aRgb = [
      Number.parseInt(aRgbHex[0], 16) as number,
      Number.parseInt(aRgbHex[1], 16) as number,
      Number.parseInt(aRgbHex[2], 16) as number,
    ];
  }
  return aRgb;
};
/**
 * Compere two colors and returns the difference between this colors
 * https://github.com/antimatter15/rgb-lab
 * https://stackoverflow.com/questions/13586999/color-difference-similarity-between-two-values-with-js
 *
 * Delta E              Perception
 * <=1.0                Not perceptible by human eyes
 * 1 - 2                Perceptible through close observation
 * 2 - 10               Perceptible at a glace
 * 10 - 49              Colors are more similar than opposite
 * 100 	                Colors are exact opposite
 * http://zschuessler.github.io/DeltaE/learn/
 */
export const deltaE = (rgbA: number[], rgbB: number[]) => {
  const labA = rgb2lab(rgbA);
  const labB = rgb2lab(rgbB);
  const deltaL = labA[0] - labB[0];
  const deltaA = labA[1] - labB[1];
  const deltaB = labA[2] - labB[2];
  const c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2]);
  const c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2]);
  const deltaC = c1 - c2;
  let deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC;
  deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH);
  const sc = 1 + 0.045 * c1;
  const sh = 1 + 0.015 * c1;
  const deltaLKlsl = deltaL / 1;
  const deltaCkcsc = deltaC / sc;
  const deltaHkhsh = deltaH / sh;
  const index = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh;
  const result = index < 0 ? 0 : Math.sqrt(index);

  return result;
};

export const rgb2lab = (rgb: number[]) => {
  let r = rgb[0] / 255,
    g = rgb[1] / 255,
    b = rgb[2] / 255,
    x,
    y,
    z;
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1;
  z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  x = x > 0.008856 ? Math.pow(x, 1 / 3) : 7.787 * x + 16 / 116;
  y = y > 0.008856 ? Math.pow(y, 1 / 3) : 7.787 * y + 16 / 116;
  z = z > 0.008856 ? Math.pow(z, 1 / 3) : 7.787 * z + 16 / 116;
  const result = [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  return result;
};

/**
 * Makes a given color represented as hex string lighter or darker.
 *
 * @see https://www.it-swarm.com.de/de/javascript/eine-hexadezimale-farbe-programmatisch-aufhellen-oder-abdunkeln-oder-rgb-und-mischfarben/971259188/
 * @see https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
 *
 * @param col string color to manipulate as RGB-Hex-String
 * @param amt number Indicates amount to make color darker or lighter.
 *  Positive value for lighter. Negative for darker.
 */
export const lightenDarkenColor = (col: string, amt: number): string => {
  if (!col.startsWith('#')) {
    col = '#' + col;
  }
  const result =
    '#' +
    col
      .replace(/^#/, '')
      .replace(/../g, (col) =>
        ('0' + Math.min(255, Math.max(0, Number.parseInt(col, 16) + amt)).toString(16)).slice(-2),
      );
  return result;
};

/**
 * Calculate relative luminance according to WCAG 2.0
 * https://www.w3.org/TR/WCAG20-TECHS/G17.html
 */
const getRelativeLuminance = (r: number, g: number, b: number): number => {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Calculate contrast ratio between two colors according to WCAG 2.0
 * https://www.w3.org/TR/WCAG20-TECHS/G17.html
 * Contrast ratio must be at least 4.5:1 for normal text (WCAG AA)
 */
const getContrastRatio = (rgb1: number[], rgb2: number[]): number => {
  const l1 = getRelativeLuminance(rgb1[0], rgb1[1], rgb1[2]);
  const l2 = getRelativeLuminance(rgb2[0], rgb2[1], rgb2[2]);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export interface ColorByID {
  colorOriginal: string;
  color: string;
  textColor: string;
}

/**
 * returns the color calculated from the ID and the corresponding text color
 * @param {sting} id guid from the object
 * @param {string} color (is optional) if a color is given, the color from the id would not be calculated
 */
export const getColorsByID = (id: string, color?: string) => {
  const hexColorFromID = color && color !== '' ? color : stringToColor(id);
  const lighterColor = lightenDarkenColor(hexColorFromID, 30);
  const hexColorToRGB = hexToRgbArray(lighterColor);

  // WCAG AA requires contrast ratio of at least 4.5:1 for normal text
  // We check against both white and black and choose the one with better contrast
  const contrastWithWhite = getContrastRatio(hexColorToRGB, [255, 255, 255]);
  const contrastWithBlack = getContrastRatio(hexColorToRGB, [0, 0, 0]);

  const textColor = contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';

  const result: ColorByID = {
    colorOriginal: hexColorFromID,
    color: lighterColor,
    textColor: textColor,
  };
  return result;
};

export interface BadgeColors {
  /** CSS color for the badge text (and border). */
  color: string;
  /** CSS color for the badge background (a light tint of the source color). */
  backgroundColor: string;
}

/**
 * Derives WCAG-AA-compliant badge colors from a source color while preserving
 * its hue (used for person-colored name badges, e.g. {@link ChatStudentBadge}).
 *
 * The background is a fixed light tint (20% of the color over white, so it is
 * theme-independent and always light). The text color starts from the source
 * color and is darkened proportionally — keeping the hue — until it meets the
 * required contrast ratio against that tint. Worst case it converges to black.
 *
 * @param hexColor source color as hex string, with or without leading "#"
 * @param minRatio minimum contrast ratio to reach (default 4.5 = WCAG AA normal text)
 */
const rgbArrayToCss = (rgb: number[]): string =>
  `rgb(${rgb.map((channel) => Math.round(channel)).join(' ')})`;

export const getAccessibleBadgeColors = (hexColor: string, minRatio = 4.5): BadgeColors => {
  const base = hexToRgbArray(hexColor.replace(/^#/, ''));
  // Light tint: 20% of the color composited over white — matches the previous
  // visual look but as a solid, opaque color that does not depend on the surface.
  const tint = base.map((channel) => Math.round(channel * 0.2 + 255 * 0.8));

  let text = [...base];
  // Guard against endless loops; ~300 * 0.95 reaches black well before the cap.
  for (let guard = 0; getContrastRatio(text, tint) < minRatio && guard < 300; guard++) {
    text = text.map((channel) => channel * 0.95);
  }

  return { color: rgbArrayToCss(text), backgroundColor: rgbArrayToCss(tint) };
};

export const rgbToHsl = (r: number, g: number, b: number): number[] => {
  r /= 255;
  g /= 255;
  b /= 255;
  const l = Math.max(r, g, b);
  const s = l - Math.min(r, g, b);
  const h = s ? (l === r ? (g - b) / s : l === g ? 2 + (b - r) / s : 4 + (r - g) / s) : 0;
  return [
    60 * h < 0 ? 60 * h + 360 : 60 * h,
    100 * (s ? (l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s))) : 0),
    (100 * (2 * l - s)) / 2,
  ];
};
